import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Activity } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/lib/i18n";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Auth() {
  const [params] = useSearchParams();
  const [mode, setMode] = useState(params.get("mode") === "register" ? "register" : "login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "login") await login(email, password);
      else await register(email, password, name);
      const next = params.get("next");
      navigate(next && next.startsWith("/") && !next.startsWith("//") ? next : "/lobby");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-base text-white flex flex-col md:flex-row">
      {/* left banner */}
      <div className="hidden md:flex md:w-1/2 relative border-r border-white/10 p-12 flex-col justify-between overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(234,179,8,0.12),transparent_55%)]" />
        <Link to="/" className="relative flex items-center gap-2">
          <Activity className="w-6 h-6 text-signal" strokeWidth={2.5} />
          <span className="font-heading font-black text-xl tracking-tight">SNETCH</span>
        </Link>
        <div className="relative">
          <h2 className="font-heading font-black text-5xl leading-[0.95] tracking-tight">
            It's a match.<br /><span className="text-signal">Not a game.</span>
          </h2>
          <p className="mt-6 font-mono text-sm text-white/50 uppercase tracking-widest">the ball is in your court</p>
        </div>
        <div className="relative font-mono text-xs text-white/30">skill over luck · 10 min · 100.000 budget</div>
      </div>

      {/* form */}
      <div className="flex-1 flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-sm"
        >
          <h1 className="font-heading font-black text-3xl tracking-tight mb-2">
            {mode === "login" ? t("login_title") : t("register_title")}
          </h1>
          <div className="w-12 h-1 bg-signal mb-8" />

          <form onSubmit={submit} className="space-y-5">
            {mode === "register" && (
              <div>
                <Label className="text-white/70 text-xs uppercase tracking-wider">{t("name")}</Label>
                <Input
                  data-testid="auth-name" value={name} onChange={(e) => setName(e.target.value)} required
                  className="mt-1.5 bg-panel border-white/10 text-white focus-visible:ring-signal rounded-sm"
                />
              </div>
            )}
            <div>
              <Label className="text-white/70 text-xs uppercase tracking-wider">{t("email")}</Label>
              <Input
                data-testid="auth-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                className="mt-1.5 bg-panel border-white/10 text-white focus-visible:ring-signal rounded-sm"
              />
            </div>
            <div>
              <Label className="text-white/70 text-xs uppercase tracking-wider">{t("password")}</Label>
              <Input
                data-testid="auth-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
                className="mt-1.5 bg-panel border-white/10 text-white focus-visible:ring-signal rounded-sm"
              />
            </div>

            {error && <p data-testid="auth-error" className="text-loss text-sm font-mono">{error}</p>}

            {mode === "login" && (
              <Link to="/forgot-password" data-testid="forgot-link" className="block text-right text-xs text-white/50 hover:text-signal transition-colors font-mono -mt-2">
                {t("forgot_link")}
              </Link>
            )}

            <Button
              data-testid="auth-submit" type="submit" disabled={loading}
              className="w-full bg-signal text-black hover:bg-white rounded-sm font-semibold h-11"
            >
              {loading ? "…" : mode === "login" ? t("login_btn") : t("register_btn")}
            </Button>
          </form>

          <button
            data-testid="auth-toggle"
            onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}
            className="mt-6 text-sm text-white/50 hover:text-signal transition-colors font-mono"
          >
            {mode === "login" ? t("to_register") : t("to_login")}
          </button>
        </motion.div>
      </div>
    </div>
  );
}
